import { MAX_FILE_HEIGHT, MAX_FILE_WIDTH, PDF_PAGES_LIMIT } from '@constants/pdf.constants';
import { CheckFileFormatReturnType } from '@domain/types/viewer';
import { getDocument, PDFWorker } from 'pdfjs-dist';
import { BinaryData, TypedArray } from 'pdfjs-dist/types/src/display/api';
import { worker } from './worker';

export async function checkFileFormat(
  data: Buffer | TypedArray,
  maxPageNumber: number = PDF_PAGES_LIMIT,
  offsetSizePage = 0,
  allowFileWithRotate = false,
  maxWidth = MAX_FILE_WIDTH,
  maxHeight = MAX_FILE_HEIGHT,
): Promise<CheckFileFormatReturnType> {
  if (Array.isArray(data)) {
    const numberOfPageList = await Promise.all(
      data.map(async (buffer) => {
        const doc = await getDocument({ data: buffer, worker, isEvalSupported: false }).promise;
        const numPages = doc?.numPages;
        doc.destroy();
        return numPages;
      }),
    );

    const numberOfPages = await numberOfPageList.reduce((a, b) => a + b, 0);

    return { tooManyPagesGrouped: numberOfPages > maxPageNumber };
  }

  let doc;
  try {
    doc = await getDocument({ data: data as BinaryData, worker, isEvalSupported: false }).promise;
  } catch (_err) {
    const freshWorker = new PDFWorker();
    try {
      doc = await getDocument({
        data: data as BinaryData,
        worker: freshWorker,
        isEvalSupported: false,
      }).promise;
    } finally {
      freshWorker.destroy();
    }
  }
  const tooManyPages = doc && doc.numPages > maxPageNumber ? doc.numPages : false;

  let hasOnePageTooBig = false;
  const effectiveMaxWidth = (1 + offsetSizePage) * maxWidth;
  const effectiveMaxHeight = (1 + offsetSizePage) * maxHeight;

  for (let i = 0; i < doc.numPages; i += 1) {
    const currentPage = await doc.getPage(i + 1);

    let width;
    let height;
    if (allowFileWithRotate && (currentPage.rotate === 270 || currentPage.rotate === 90)) {
      width = currentPage.view[3] - currentPage.view[1];
      height = currentPage.view[2] - currentPage.view[0];
    } else {
      width = currentPage.view[2] - currentPage.view[0];
      height = currentPage.view[3] - currentPage.view[1];
    }

    const fitsPortrait = width <= effectiveMaxWidth && height <= effectiveMaxHeight;
    const fitsLandscape = width <= effectiveMaxHeight && height <= effectiveMaxWidth;
    if (!fitsPortrait && !fitsLandscape) {
      hasOnePageTooBig = true;
      break;
    }
  }

  doc.destroy();

  return { tooManyPages, hasOnePageTooBig };
}
